"use client";

import { useState } from "react";

const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";
const URB = "var(--font-urbanist), sans-serif";

interface Choice {
  label: string;
  text: string;
}

interface QuestionCardProps {
  questionNumber: number;
  totalQuestions: number;
  title: string;
  choices: Choice[];
  onAnswer?: (choiceIndex: number) => void;
  onSkip?: () => void;
  onSkipAll?: () => void;
  onTextAnswer?: (text: string) => void;
}

export default function QuestionCard({
  questionNumber,
  totalQuestions,
  title,
  choices,
  onAnswer,
  onSkip,
  onSkipAll,
  onTextAnswer,
}: QuestionCardProps) {
  const [textValue, setTextValue] = useState("");
  const hasText = textValue.trim().length > 0;

  const submitText = () => {
    const t = textValue.trim();
    if (!t) return;
    onTextAnswer?.(t);
    setTextValue("");
  };
  return (
    <div
      className="desktop-glass-question-card"
      style={{
        background: "#1E1E1E",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 16,
        boxShadow: "0px 25px 50px -12px rgba(0,0,0,0.4)",
        padding: 1,
        overflow: "clip",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        alignItems: "center",
        width: 720,
      }}
    >
      {/* Header */}
      <div style={{ width: "100%", paddingTop: 20, paddingBottom: 16, paddingLeft: 24, paddingRight: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4.5, alignItems: "flex-start" }}>
          <p
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: "2px",
              lineHeight: "15px",
              color: "#9E79D2",
              textTransform: "uppercase",
              margin: 0,
            }}
          >
            QUESTION {questionNumber} OF {totalQuestions}
          </p>
          <p
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 18,
              lineHeight: "28px",
              color: "#F2F0ED",
              margin: 0,
            }}
          >
            {title}
          </p>
        </div>
      </div>

      {/* Choice rows */}
      {choices.map((choice, i) => (
        <button
          key={i}
          onClick={() => onAnswer?.(i)}
          className="app-choice desktop-glass-question-choice"
          style={{
            background: "#2D2C2C",
            borderRadius: 12,
            width: 694,
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <p style={{ fontFamily: PJK, fontSize: 18, lineHeight: "28px", margin: 0 }}>
            <span style={{ fontWeight: 500, color: "#8F8E8C" }}>{choice.label}:</span>
            <span style={{ fontWeight: 700, color: "#F2F0ED" }}> {choice.text}</span>
          </p>
        </button>
      ))}

      {/* Textarea */}
      <div style={{ width: 718, paddingTop: 12, paddingBottom: 72, paddingLeft: 12, paddingRight: 12 }}>
        <textarea
          placeholder="Or type your answer..."
          rows={1}
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submitText();
            }
          }}
          className="app-textarea"
          style={{
            width: "100%",
            minHeight: 44,
            maxHeight: 160,
            padding: "10px 12px",
            background: "transparent",
            border: "none",
            outline: "none",
            resize: "none",
            fontFamily: URB,
            fontWeight: 400,
            fontSize: 16,
            lineHeight: "24px",
            color: "#F2F0ED",
          }}
        />
      </div>

      {/* Footer */}
      <div
        className="desktop-glass-question-footer"
        style={{
          background: "#2D2C2C",
          width: "100%",
          paddingLeft: 24,
          paddingRight: 16,
          paddingTop: 16,
          paddingBottom: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <button
            onClick={onSkip}
            className="app-skip-btn"
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: "0.6px",
              lineHeight: "16px",
              color: "rgba(242,240,237,0.5)",
              textTransform: "uppercase",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            SKIP
          </button>
          <button
            onClick={onSkipAll}
            className="app-skip-btn"
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: "0.6px",
              lineHeight: "16px",
              color: "rgba(242,240,237,0.5)",
              textTransform: "uppercase",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            SKIP ALL
          </button>
        </div>

        <button
          onClick={hasText ? submitText : undefined}
          aria-label="Send answer"
          disabled={!hasText}
          className="app-send-btn"
          style={{
            width: 40,
            height: 40,
            borderRadius: 14,
            background: "#8635F6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            cursor: hasText ? "pointer" : "default",
            opacity: hasText ? 1 : 0.6,
            transition: "opacity 150ms ease-out",
          }}
        >
          <img src="/app-icons/location-arrow.svg" alt="" style={{ width: 18, height: 18 }} />
        </button>
      </div>
    </div>
  );
}
